import { useEffect, useRef, useState } from 'react'

import {
  $isTableCellNode,
  $isTableRowNode,
  $isTableSelection,
  TableCellNode,
  TableNode,
  TableRowNode,
  TableSelection
} from '@lexical/table'
import { mergeRegister } from '@lexical/utils'
import { useMemoizedFn } from 'ahooks'
import { createStyles } from 'antd-style'
import { $getSelection, COMMAND_PRIORITY_EDITOR, LexicalEditor } from 'lexical'

import { useEditor } from '../../hooks/useEditor'
import { useSelectionChange } from '../../hooks/useSelectionChange'
import { $isTableControllerNode } from '../../nodes/TableControllerNode'
import { $isTableWrapperNode } from '../../nodes/TableWrapperNode'
import {
  ACTIVE_TABLE_CHANGE_COMMAND,
  TABLE_MARK_UPDATE_COMMAND,
  TABLE_SIZE_CHANGE_COMMAND
} from '../../plugins/TableControllerPlugin/commands'
import { TableMarkType } from '../../plugins/TableControllerPlugin/interface'
import { $findParentNode } from '../../utils/node'
import {
  $makeTableColHighlight,
  $makeTableHighlight,
  $makeTableRowHighlight
} from '../../utils/table'

import {
  TABLE_COL_BAR_CLICK_COMMAND,
  TABLE_CORNER_BAR_CLICK_COMMAND,
  TABLE_ROW_BAR_CLICK_COMMAND
} from './command'
import { TableControllerProps } from './interface'

const BAR_SIZE = 14

const useStyles = createStyles(({ token }) => ({
  bar: {
    position: 'absolute',
    top: -BAR_SIZE,
    left: -BAR_SIZE,
    backgroundColor: '#f4f5f5',
    borderWidth: '1px 0 0 1px',
    borderStyle: 'solid',
    borderColor: 'var(--lexical-table-border-color)',
    outline: 'none',
    cursor: 'pointer',
    [`&:hover`]: {
      backgroundColor: '#e7e9e8'
    },
    [`&.is-selected`]: {
      backgroundColor: token.blue4
    },
    [`&.is-marked`]: {
      backgroundColor: token.red4
    }
  },
  colBar: {
    height: BAR_SIZE,
    [`&.is-last`]: {
      borderRightWidth: 1,
      borderTopRightRadius: token.borderRadius
    }
  },
  rowBar: {
    width: BAR_SIZE,
    [`&.is-last`]: {
      borderBottomWidth: 1,
      borderBottomLeftRadius: token.borderRadius
    }
  },
  cornerBar: {
    width: BAR_SIZE,
    height: BAR_SIZE,
    borderTopLeftRadius: token.borderRadius
  }
}))

/**
 * 计算 table 的行列尺寸信息
 */
export const computeTableSizeGrid = (
  editor: LexicalEditor,
  tableNode: TableNode
) => {
  const readonlyColWidths = tableNode.getColWidths() || []
  // 补偿 border width
  const colWidths = readonlyColWidths.map((width, index) => {
    return width + (index === readonlyColWidths.length - 1 ? 1 : 0)
  })
  const rowNodes = tableNode.getChildren().filter($isTableRowNode)
  const rowHeights = rowNodes.map((rowNode, index) => {
    const el = editor.getElementByKey(rowNode.getKey())
    if (!el) return 0
    return (
      el.getBoundingClientRect().height +
      // 补偿 border width
      (index === rowNodes.length - 1 ? 1 : 0)
    )
  })
  return {
    colWidths,
    rowHeights
  }
}

/**
 * 获取 markedCellNodes 在 tableNode 中的行列索引
 */
const getMarkedIndexes = (
  tableNode: TableNode,
  markType: TableMarkType,
  markedCellNodes: TableCellNode[]
) => {
  const allTableRowNodes = tableNode.getChildren().filter($isTableRowNode)
  const markedRowNodes = markedCellNodes.map(cellNode =>
    cellNode.getParentOrThrow<TableRowNode>()
  )
  const getMarkedRowIndexes = () => {
    const markedRowIndexSet = new Set(
      markedRowNodes.map(rowNode => allTableRowNodes.indexOf(rowNode))
    )
    return Array.from(markedRowIndexSet)
  }
  const getMarkedColIndexes = () => {
    const colIndexSet = new Set<number>()
    for (const rowNode of allTableRowNodes) {
      const cellNodes = rowNode.getChildren().filter($isTableCellNode)
      for (const cellNode of cellNodes) {
        if (markedCellNodes.includes(cellNode)) {
          colIndexSet.add(cellNodes.indexOf(cellNode))
        }
      }
    }
    return Array.from(colIndexSet)
  }
  if (markType === 'row') {
    return {
      markedRowIndexes: getMarkedRowIndexes(),
      markedColIndexes: []
    }
  }

  if (markType === 'col') {
    return {
      markedRowIndexes: [],
      markedColIndexes: getMarkedColIndexes()
    }
  }

  return {
    markedRowIndexes: getMarkedRowIndexes(),
    markedColIndexes: getMarkedColIndexes()
  }
}

const isTableActived = (
  editor: LexicalEditor,
  active: boolean,
  tableNode: TableNode | undefined
): tableNode is TableNode => {
  if (active && tableNode) {
    // 不能用 editor.getEditorState().read 不然更新会延迟
    return editor.read(() => {
      return tableNode.isAttached()
    })
  }
  return false
}

export const TableController = ({
  tableControllerNodeKey
}: TableControllerProps) => {
  const editor = useEditor()
  const tableNodeRef = useRef<TableNode | undefined>(undefined)

  const activeRef = useRef(false)
  const [{ colWidths, rowHeights }, setGrid] = useState({
    colWidths: [] as number[],
    rowHeights: [] as number[]
  })
  const [
    { selectedColIndexes: selectedColIndexes, selectedRowIndexes },
    setSelectedIndexes
  ] = useState({
    selectedColIndexes: [] as number[],
    selectedRowIndexes: [] as number[]
  })
  const [{ markedColIndexes, markedRowIndexes }, setMarkedIndexes] = useState({
    markedColIndexes: [] as number[],
    markedRowIndexes: [] as number[]
  })

  const updateGrid = useMemoizedFn(() => {
    if (isTableActived(editor, activeRef.current, tableNodeRef.current)) {
      setGrid(computeTableSizeGrid(editor, tableNodeRef.current))
    } else {
      setGrid({ colWidths: [], rowHeights: [] })
    }
    return false
  })

  const mouseoverRef = useRef(false)

  // 追踪 table 变化
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        ACTIVE_TABLE_CHANGE_COMMAND,
        activeTableNode => {
          if (activeTableNode) {
            const tableWrapperNode = $findParentNode(
              activeTableNode,
              $isTableWrapperNode
            )
            if (tableWrapperNode) {
              const tableControllerNode = tableWrapperNode
                .getChildren()
                .find($isTableControllerNode)
              if (tableControllerNode?.getKey() === tableControllerNodeKey) {
                tableNodeRef.current = activeTableNode
              }
            }
          }
          activeRef.current =
            mouseoverRef.current || activeTableNode === tableNodeRef.current
          updateGrid()
          return false
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        TABLE_SIZE_CHANGE_COMMAND,
        updateGrid,
        COMMAND_PRIORITY_EDITOR
      )
    )
  }, [editor, tableControllerNodeKey, updateGrid])

  // 根据 selection 设置 selected 样式
  useSelectionChange(() => {
    if (isTableActived(editor, activeRef.current, tableNodeRef.current)) {
      const resetSelectedIndexes = () => {
        setSelectedIndexes({
          selectedColIndexes: [],
          selectedRowIndexes: []
        })
      }
      const resolveSelectedIndexes = (
        tableNode: TableNode,
        selection: TableSelection
      ) => {
        const allTableRowNodes = tableNode.getChildren().filter($isTableRowNode)
        const nodes = selection.getNodes()
        const selectedTableRowNodes = nodes.filter($isTableRowNode)
        const selectedTableCellNodes = nodes.filter($isTableCellNode)
        const selectedRowIndexes: number[] = []
        for (const [index, rowNode] of Object.entries(allTableRowNodes)) {
          if (selectedTableRowNodes.includes(rowNode)) {
            const cellNodes = rowNode.getChildren().filter($isTableCellNode)
            if (
              selectedTableCellNodes.includes(cellNodes[0]) &&
              selectedTableCellNodes.includes(cellNodes[cellNodes.length - 1])
            ) {
              selectedRowIndexes.push(Number.parseInt(index))
            }
          }
        }

        const firstRowCellNodes = allTableRowNodes[0]
          .getChildren()
          .filter($isTableCellNode)
        const firstRowSelectedColIndexes: number[] = []
        for (const [index, cellNode] of Object.entries(firstRowCellNodes)) {
          if (selectedTableCellNodes.includes(cellNode)) {
            firstRowSelectedColIndexes.push(Number.parseInt(index))
          }
        }
        const lastRowCellNodes = allTableRowNodes[allTableRowNodes.length - 1]
          .getChildren()
          .filter($isTableCellNode)
        const lastRowSelectedColIndexes: number[] = []
        for (const [index, cellNode] of Object.entries(lastRowCellNodes)) {
          if (selectedTableCellNodes.includes(cellNode)) {
            lastRowSelectedColIndexes.push(Number.parseInt(index))
          }
        }
        const selectedColIndexes: number[] = firstRowSelectedColIndexes.filter(
          value => lastRowSelectedColIndexes.includes(value)
        )
        setSelectedIndexes({
          selectedColIndexes: selectedColIndexes,
          selectedRowIndexes
        })
      }
      const selection = $getSelection()
      if (!$isTableSelection(selection)) {
        resetSelectedIndexes()
        return false
      }
      resolveSelectedIndexes(tableNodeRef.current, selection)
    }
    return false
  })

  // 设置 marked 样式
  useEffect(() => {
    return editor.registerCommand(
      TABLE_MARK_UPDATE_COMMAND,
      ({ type, marked, cellNodes }) => {
        if (isTableActived(editor, activeRef.current, tableNodeRef.current)) {
          if (marked) {
            setMarkedIndexes(
              getMarkedIndexes(tableNodeRef.current, type, cellNodes)
            )
          } else {
            setMarkedIndexes({
              markedColIndexes: [],
              markedRowIndexes: []
            })
          }
        }
        return false
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor])

  const { cx, styles } = useStyles()

  const colDoms = colWidths.map((width, index) => {
    const left = colWidths.slice(0, index).reduce((acc, cur) => acc + cur, 0)
    return (
      <div
        key={index}
        role='button'
        tabIndex={0}
        className={cx(styles.bar, styles.colBar, {
          ['is-last']: index === colWidths.length - 1,
          ['is-selected']: selectedColIndexes.includes(index),
          ['is-marked']: markedColIndexes.includes(index)
        })}
        style={{
          width,
          left
        }}
        onClick={() => {
          const tableNode = tableNodeRef.current
          if (isTableActived(editor, activeRef.current, tableNode)) {
            editor.update(() => {
              $makeTableColHighlight(editor, tableNode, index)
            })
            editor.dispatchCommand(TABLE_COL_BAR_CLICK_COMMAND, {
              index,
              left,
              width
            })
          }
        }}
        onMouseEnter={() => {
          mouseoverRef.current = true
        }}
        onMouseLeave={() => {
          mouseoverRef.current = false
        }}
      />
    )
  })

  const rowDoms = rowHeights.map((height, index) => {
    const top = rowHeights.slice(0, index).reduce((acc, cur) => acc + cur, 0)
    return (
      <div
        key={index}
        role='button'
        tabIndex={0}
        className={cx(styles.bar, styles.rowBar, {
          ['is-last']: index === rowHeights.length - 1,
          ['is-selected']: selectedRowIndexes.includes(index),
          ['is-marked']: markedRowIndexes.includes(index)
        })}
        style={{
          height,
          top
        }}
        onClick={() => {
          const tableNode = tableNodeRef.current
          if (isTableActived(editor, activeRef.current, tableNode)) {
            editor.update(() => {
              $makeTableRowHighlight(editor, tableNode, index)
            })
            editor.dispatchCommand(TABLE_ROW_BAR_CLICK_COMMAND, {
              index,
              top,
              height
            })
          }
        }}
        onMouseEnter={() => {
          mouseoverRef.current = true
        }}
        onMouseLeave={() => {
          mouseoverRef.current = false
        }}
      />
    )
  })

  const cornerDom =
    colWidths.length && rowHeights.length ? (
      <div
        role='button'
        tabIndex={0}
        className={cx(styles.bar, styles.cornerBar, {
          ['is-selected']:
            selectedColIndexes.length === colWidths.length &&
            selectedRowIndexes.length === rowHeights.length,
          ['is-marked']:
            markedColIndexes.length === colWidths.length &&
            markedRowIndexes.length === rowHeights.length
        })}
        onClick={() => {
          const tableNode = tableNodeRef.current
          if (isTableActived(editor, activeRef.current, tableNode)) {
            editor.update(() => {
              $makeTableHighlight(editor, tableNode)
            })
            editor.dispatchCommand(TABLE_CORNER_BAR_CLICK_COMMAND, undefined)
          }
        }}
        onMouseEnter={() => {
          mouseoverRef.current = true
        }}
        onMouseLeave={() => {
          mouseoverRef.current = false
        }}
      />
    ) : null

  return (
    <>
      {cornerDom}
      {colDoms}
      {rowDoms}
    </>
  )
}
